import { createSelector, ThunkAction, UnknownAction } from "@reduxjs/toolkit"
import { facetsSelectors } from "./facets-slice"
import { filtersSelectors } from "./filters-slice"
import { paginationSelectors } from "./pagination-slice"
import { SelectorStateInterface } from "./type-utils"
import { searchAsyncThunk } from "./search-actions"
import SearchResponseSchema, { SearchResponse } from "./search-response"

export type RootState = SelectorStateInterface<
  typeof facetsSelectors.selectQueryFacets
> &
  SelectorStateInterface<typeof filtersSelectors.selectQueryFilters> &
  SelectorStateInterface<typeof paginationSelectors.selectQueryPagination>

export type SearchThunkOptions = { source?: string[]; options: object }
export type SearchQuery = ReturnType<typeof selectSearchQuery>
export const selectSearchQuery = createSelector(
  [(state) => state, (_state, options: SearchThunkOptions) => options],
  (state, { source = [], options }) => ({
    source,
    facets: facetsSelectors.selectQueryFacets(state),
    filters: filtersSelectors.selectQueryFilters(state),
    options,
    ...paginationSelectors.selectQueryPagination(state),
  }),
)

export interface ISearchClient {
  (
    requestBodyFactory: () => SearchQuery,
    handlers: {
      onResolved(responseBody: any): void
      onRejected(error: any): void
      onInvoked(): void
      onAborted(): void
    },
  ): void
}

type SearchThunkAction = ThunkAction<
  Promise<SearchResponse>,
  RootState,
  { searchClient: ISearchClient },
  UnknownAction
>
export function search(options: SearchThunkOptions): SearchThunkAction {
  return function (dispatch, getState, { searchClient }) {
    const { promise, resolve, reject } = Promise.withResolvers<SearchResponse>()

    searchClient(() => selectSearchQuery(getState(), options), {
      onInvoked() {
        dispatch(searchAsyncThunk(promise))
      },

      onAborted() {
        reject(new Error("search aborted"))
      },

      onResolved(responseBody: any) {
        const parseResult = SearchResponseSchema.safeParse(responseBody)
        if (parseResult.success) {
          resolve(parseResult.data)
        } else {
          reject(
            new Error("invalid search response", { cause: parseResult.error }),
          )
        }
      },

      onRejected(error) {
        reject(new Error("search failed", { cause: error }))
      },
    })

    return promise
  }
}
